import { Modal } from "@/components/Modal"
import { getArchiveClusters } from "@/domain/interbu/getArchiveClusters"
import { STORAGE_INTERBU_BY_CLUSTER_PATH } from "@/domain/resources/constants"
import useGetUserInfos from "@/hooks/useGetUserInfos"
import { type MediaJsonld, useListFile } from "@/networking/api"
import { getFilename } from "@/utils/files/file"
import { isNotNullNorUndefined } from "@/utils/ts-utils"
import { Box, Flex, ModalBody, Spinner } from "@chakra-ui/react"
import { useMemo } from "react"
import { FormattedMessage } from "react-intl"
import { DownloadFileButton } from "../Buttons/DownloadFileButton"
import { P2B } from "../Typographies"

type Props = {
  isOpen: boolean
  onClose: () => void
}

export default function ShareInterBUArchivesModal({ isOpen, onClose }: Props) {
  const { data, isLoading, isFetching } = useListFile(STORAGE_INTERBU_BY_CLUSTER_PATH)
  const isSpinner = useMemo(() => isLoading || isFetching, [isLoading, isFetching])
  const listFiles = useMemo(() => data?.["hydra:member"] ?? [], [data])

  const { data: userInfos } = useGetUserInfos()
  const clustersAsMember = useMemo(() => getArchiveClusters(userInfos?.collaborators), [userInfos])

  const { received, sent } = useMemo(() => {
    const r = [] as { cluster: string; doc: MediaJsonld }[]
    const s = [] as { cluster: string; doc: MediaJsonld }[]
    clustersAsMember.map(c => {
      const docRName = `interbu-received-${c.id}.xlsx`
      const docR = listFiles
        .filter(d => getFilename(d.path).toString() === docRName.toString())
        .shift()
      if (isNotNullNorUndefined(docR)) r.push({ cluster: c.name, doc: docR })

      const docSName = `interbu-sended-${c.id}.xlsx`
      const docS = listFiles.filter(d => getFilename(d.path) === docSName).shift()
      if (isNotNullNorUndefined(docS)) s.push({ cluster: c.name, doc: docS })
    })
    return { received: r, sent: s }
  }, [clustersAsMember, listFiles])

  return (
    <Modal
      {...{ isOpen, onClose }}
      title={<FormattedMessage id="userSharedData.interbu.archives" />}
      scrollBehavior="outside"
    >
      <ModalBody>
        <Flex flexDir="column" gap="6" pb={12}>
          <Box as="div">
            <FormattedMessage id="userSharedData.interbu.archives.message" />
          </Box>

          {isSpinner && <Spinner />}
          {!isSpinner && received.length < 1 && sent.length < 1 && (
            <Box as="div">
              <FormattedMessage id="userSharedData.interbu.archives.empty" />
            </Box>
          )}
          {!isSpinner && received.length >= 1 && (
            <Box as="div">
              <P2B pb={2}>
                <FormattedMessage id="userSharedData.interbu.archives.received" />
              </P2B>
              {received.map(file => (
                <Box key={file.doc.id} pt={2}>
                  <DownloadFileButton file={file.doc} hideInfos={true}>
                    {file.cluster}
                  </DownloadFileButton>
                </Box>
              ))}
            </Box>
          )}
          {!isSpinner && sent.length >= 1 && (
            <Box as="div">
              <P2B pb={2}>
                <FormattedMessage id="userSharedData.interbu.archives.sent" />
              </P2B>
              {sent.map(file => (
                <Box key={file.doc.id} pt={2}>
                  <DownloadFileButton file={file.doc} hideInfos={true}>
                    {file.cluster}
                  </DownloadFileButton>
                </Box>
              ))}
            </Box>
          )}
        </Flex>
      </ModalBody>
    </Modal>
  )
}
